import React, { useState, useEffect } from 'react';
import {
    Button,
    TextField,
    Autocomplete,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../../resources/strings/StringKeys';
import { IconPlus } from '@tabler/icons-react';
import { useUserContext } from '../../../../context/user_context/UserContext';
import { ToastType, useToast } from '../../../../context/toast/ToastContext';
import { useLoadingContext } from '../../../../context/loading/LoadingContext';
import { createPerformanceReview, getPerformanceReview } from '../../../../repository/PerformanceReviewDAO';
import { CreateReviewTemplateModal } from '../PerformanceReviewTemplates/modals/CreateReviewTemplateModal';
import usePermissions from '../../../../utils/permissions/UsePermissions';
import { Modules } from '../../../../utils/permissions/Modules';
import { PermissionTypes } from '../../../../utils/permissions/PermissionTypes';
import { useOrganizationContext } from '../../../../context/organization_context/OrganizationContext';
import BaseModal from '../../../../components/modals/BaseModal';

export const InitiateReviewModal = ({ open, onClose, templates, employees }) => {
    const { t } = useTranslation();
    const { firebaseUser } = useUserContext();
    const { getEmployeeByID, isUserDepartmentManager, isOwner } = useOrganizationContext();
    const [formData, setFormData] = useState({
        employee: '',
        template: '',
        reviewCycle: '',
        dueDate: ''
    });
    const [employeeError, setEmployeeError] = useState(false);
    const [templateError, setTemplateError] = useState(false);
    const [reviewCycleError, setReviewCycleError] = useState(false);
    const [dueDateError, setDueDateError] = useState(false);
    const [loading, setLoading] = useState(false);
    const { setLoadingData } = useLoadingContext();
    const { showToast } = useToast();
    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
    const { hasPermission } = usePermissions();

    useEffect(() => {
        if (!open) {
            resetForm();
        }
    }, [open]);

    const checkValidations = () => {
        let valid = true;
        if (!formData.employee) {
            setEmployeeError(true);
            showToast(t(StringKeys.EMPLOYEE_IS_REQUIRED), ToastType.DANGER);
            valid = false;
        }

        // Check if user has permission to review this employee
        const isManager = isUserDepartmentManager(firebaseUser?.uid);
        const selectedEmployee = getEmployeeByID(formData.employee);
        if (!isOwner && isManager && selectedEmployee?.department_id !== getEmployeeByID(firebaseUser.uid)?.department_id) {
            setEmployeeError(true);
            showToast(t(StringKeys.NO_PERMISSION_TO_REVIEW_EMPLOYEE), ToastType.DANGER);
            valid = false;
        }

        if (!formData.template) {
            setTemplateError(true);
            showToast(t(StringKeys.REVIEW_TEMPLATE_IS_REQUIRED), ToastType.DANGER);
            valid = false;
        }
        if (!formData.reviewCycle) {
            setReviewCycleError(true);
            showToast(t(StringKeys.REVIEW_CYCLE_IS_REQUIRED), ToastType.DANGER);
            valid = false;
        }
        if (!formData.dueDate) {
            setDueDateError(true);
            showToast(t(StringKeys.DUE_DATE_IS_REQUIRED), ToastType.DANGER);
            valid = false;
        } else {
            const dueDate = new Date(formData.dueDate);
            if (isNaN(dueDate.getTime())) {
                setDueDateError(true);
                showToast(t(StringKeys.INVALID_DUE_DATE), ToastType.DANGER);
                valid = false;
            } else {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                if (dueDate < today) {
                    setDueDateError(true);
                    showToast(t(StringKeys.DUE_DATE_MUST_BE_IN_FUTURE), ToastType.DANGER);
                    valid = false;
                }
                const twoYearsFromNow = new Date();
                twoYearsFromNow.setFullYear(twoYearsFromNow.getFullYear() + 2);
                if (dueDate > twoYearsFromNow) {
                    setDueDateError(true);
                    showToast(t(StringKeys.DUE_DATE_TOO_FAR), ToastType.DANGER);
                    valid = false;
                }
            }
        }
        return valid;
    };

    const handleSubmit = async () => {
        if (!checkValidations()) return;

        setLoading(true);
        try {
            setLoadingData(true);

            const reviewTemplateResponse = await getPerformanceReview(firebaseUser.organization, formData.template);
            let templateData;
            if (reviewTemplateResponse.success) {
                templateData = reviewTemplateResponse.data;
            } else {
                showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
                return;
            }

            const questionsWithResponses = templateData.questions.map(question => ({
                ...question,
                responses: {
                    employee: { answer: "" },
                    manager: { answer: "" },
                },
            }));

            const period = formData.reviewCycle.split('_')[1];
            const employee = getEmployeeByID(formData.employee);
            const title = `${employee.first_name} ${employee.last_name} - ${period === 'ANNUAL' ? 'Annual Review' : `${period} Review`}`;

            const result = await createPerformanceReview(
                firebaseUser.organization,
                formData.employee,
                firebaseUser.uid,
                formData.reviewCycle,
                questionsWithResponses,
                formData.dueDate,
                title
            );

            if (result.success) {
                showToast(t(StringKeys.SUCCESS), ToastType.SUCCESS);
                resetForm();
                onClose();
            } else {
                showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
            }
        } catch (error) {
            showToast(t(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN), ToastType.DANGER);
        } finally {
            setLoadingData(false);
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            employee: '',
            template: '',
            reviewCycle: '',
            dueDate: ''
        });
        setEmployeeError(false);
        setTemplateError(false);
        setReviewCycleError(false);
        setDueDateError(false);
    };

    const generateReviewCycleOptions = () => {
        const currentYear = new Date().getFullYear();
        const nextYear = currentYear + 1;
        
        return [
            { value: `${currentYear}_Q1`, label: `${t(StringKeys.Q1)} ${currentYear}` },
            { value: `${currentYear}_Q2`, label: `${t(StringKeys.Q2)} ${currentYear}` },
            { value: `${currentYear}_Q3`, label: `${t(StringKeys.Q3)} ${currentYear}` },
            { value: `${currentYear}_Q4`, label: `${t(StringKeys.Q4)} ${currentYear}` },
            { value: `${currentYear}_ANNUAL`, label: `${t(StringKeys.ANNUAL)} ${currentYear}` },
            ...(new Date().getMonth() >= 9 ? [
                { value: `${nextYear}_Q1`, label: `${t(StringKeys.Q1)} ${nextYear}` }
            ] : [])
        ];
    };

    const today = new Date().toISOString().split('T')[0];
    const twoYearsFromNow = new Date();
    twoYearsFromNow.setFullYear(twoYearsFromNow.getFullYear() + 2);
    const maxDate = twoYearsFromNow.toISOString().split('T')[0];

    return (
        <>
            <BaseModal
                open={open}
                onClose={() => {
                    resetForm();
                    onClose();
                }}
                maxWidth='sm'
                title={t(StringKeys.INITIATE_PERFORMANCE_REVIEW)}
                actions={
                    <>
                        <Button onClick={() => {
                            resetForm();
                            onClose();
                        }}>{t(StringKeys.CANCEL)}</Button>
                        <Button 
                            onClick={handleSubmit} 
                            variant="contained" 
                            disabled={loading}
                        >
                            {t(StringKeys.SUBMIT)}
                        </Button>
                    </>
                }
            >
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <FormControl fullWidth error={employeeError}>
                            <InputLabel>{t(StringKeys.EMPLOYEE)}</InputLabel>
                            <Select
                                value={formData.employee}
                                label={t(StringKeys.EMPLOYEE)}
                                onChange={(e) => {
                                    setFormData(prev => ({ ...prev, employee: e.target.value }));
                                    setEmployeeError(false);
                                }}
                            >
                                {employees
                                    .filter(employee => {
                                        // Don't allow reviewing yourself
                                        if (employee.id === firebaseUser.uid) return false;
                                        
                                        // Owner can review anyone
                                        if (firebaseUser.role === 'Owner') return true;
                                        
                                        // Users with write all reviews permission can review anyone
                                        if (hasPermission(Modules.PERFORMANCE_REVIEWS, PermissionTypes.READ_AND_WRITE_ALL_REVIEWS)) {
                                            return true;
                                        }
                                        
                                        // Department managers with managed employees permission can only review their department
                                        if (hasPermission(Modules.PERFORMANCE_REVIEWS, PermissionTypes.READ_AND_WRITE_REVIEWS_FOR_MANAGED_EMPLOYEES) &&
                                            isUserDepartmentManager(firebaseUser.uid)) {
                                            const managerDepartmentId = getEmployeeByID(firebaseUser.uid)?.department_id;
                                            return employee.department_id === managerDepartmentId;
                                        }
                                        
                                        return false;
                                    })
                                    .map((employee) => (
                                        <MenuItem key={employee.id} value={employee.id}>
                                            {employee.first_name} {employee.last_name}
                                        </MenuItem>
                                    ))
                                }
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth error={templateError}>
                            <InputLabel>{t(StringKeys.REVIEW_TEMPLATE)}</InputLabel>
                            <Select
                                value={formData.template}
                                label={t(StringKeys.REVIEW_TEMPLATE)}
                                onChange={(e) => {
                                    if (e.target.value === 'addTemplate') {
                                        setIsTemplateModalOpen(true);
                                    } else {
                                        setFormData(prev => ({ ...prev, template: e.target.value }));
                                        setTemplateError(false);
                                    }
                                }}
                            >
                                {templates.map((template) => (
                                    <MenuItem key={template.id} value={template.id}>
                                        {template.name}
                                    </MenuItem>
                                ))}
                                {hasPermission(Modules.PERFORMANCE_REVIEWS, PermissionTypes.WRITE) && (
                                    <MenuItem key="addTemplate" value="addTemplate">
                                        <IconPlus/> {t(StringKeys.CREATE_REVIEW_TEMPLATE)}
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth error={reviewCycleError}>
                            <InputLabel>{t(StringKeys.REVIEW_CYCLE)}</InputLabel>
                            <Select
                                value={formData.reviewCycle}
                                label={t(StringKeys.REVIEW_CYCLE)}
                                onChange={(e) => {
                                    setFormData(prev => ({ ...prev, reviewCycle: e.target.value }));
                                    setReviewCycleError(false);
                                }}
                            >
                                {generateReviewCycleOptions().map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            type="date"
                            label={t(StringKeys.DUE_DATE)}
                            InputLabelProps={{ shrink: true }}
                            value={formData.dueDate}
                            onChange={(e) => {
                                setFormData(prev => ({ ...prev, dueDate: e.target.value }));
                                setDueDateError(false);
                            }}
                            error={dueDateError}
                            required
                            inputProps={{
                                min: today,
                                max: maxDate
                            }}
                        />
                    </Grid>
                </Grid>
            </BaseModal>
            <CreateReviewTemplateModal
                open={isTemplateModalOpen}
                onHide={() => setIsTemplateModalOpen(false)}
            />
        </>
    );
};
